import { useCallback } from 'react';
import { odata } from '@bingads-webui/http-util';
import { useOdataOptions } from './use-odata-options';
import type { AigcRequestActivity } from './use-generate-business-snapshot';
import type { UploadedImageInfo } from './use-asset-library-image-upload';

const API_NAME = 'odataapi/campaign/v2/customers/accounts/folders/default.getrootfolder';

interface AssetEntity {
  Id?: string | number;
  Type?: string;
  SourceType?: string;
  Text?: string;
  Name?: string;
  Url?: string;
}

interface FolderResponse {
  Assets?: AssetEntity[];
}

/**
 * Page size for the `GetRootFolder` asset pagination loop. `GetRootFolder`
 * applies `$top`/`$skip` to the root folder's `Assets` collection, so a single
 * request only returns the first page of assets.
 */
const PAGE_SIZE = 20;

/**
 * Safety cap on the number of pages fetched, guarding against a runaway loop
 * if the server ever ignores `$skip`. 100 pages × 20 = 2000 assets — well
 * beyond any realistic root-folder image count, so it never triggers in
 * practice.
 */
const MAX_PAGES = 100;

type FetchAccountImages = (activity?: AigcRequestActivity) => Promise<UploadedImageInfo[]>;

/**
 * Hook that fetches the user's account-level images from the Asset Library
 * via `Folders/Default.GetRootFolder`. Used by the agentic onboarding
 * `load()` to rehydrate previously uploaded images on resume.
 *
 * Returns only `GenericImage` assets that have both a URL and a display
 * name (Text). Assets without a Text value are dropped because they would
 * render as blank tiles in the gallery.
 *
 * Client still drops entries without a `Text` value to avoid rendering
 * legacy/incomplete records that have no display name.
 *
 * `GetRootFolder` applies `$top`/`$skip` to the root folder's `Assets`
 * collection, so the request is paginated: assets are fetched page by page
 * until a short page is returned. This ensures accounts with more than one
 * page of root-folder images are fully rehydrated rather than capped at the
 * first page.
 *
 * @param {string} [filter] - optional OData `$filter` expression; when
 *   truthy, `WebsiteImported` assets are excluded client-side so the
 *   page and dropping entries client-side once the response arrives.
 * @returns {Function} fetchAccountImages — accepts an optional activity
 *   logger, returns a Promise.
 */
export const useAssetLibraryGetImages = (filter?: string): FetchAccountImages => {
  const [odataOptions, { cid, aid }] = useOdataOptions();

  return useCallback(async (activity?: AigcRequestActivity): Promise<UploadedImageInfo[]> => {
    if (!cid || !aid) return [];

    const allAssets: AssetEntity[] = [];

    for (let page = 0; page < MAX_PAGES; page += 1) {
      const skip = page * PAGE_SIZE;
      const url = `/Customers(${cid})/Accounts(${aid})/Folders/Default.GetRootFolder?$select=Id,ParentFolderId,Name,ItemsCount&$expand=Assets&$top=${PAGE_SIZE}&$skip=${skip}&count=true`;

      // eslint-disable-next-line no-await-in-loop
      const response: FolderResponse = await odata.get(url, {
        ...odataOptions,
        // Only failures are logged here — the caller already traces the
        // rehydrated count, and a success line per page would spam the loop.
        complete: (jqXHR: any, textStatus: string) => {
          if (!activity) return;
          const isSuccess = textStatus === 'success'
            || textStatus === 'notmodified'
            || textStatus === 'nocontent';
          if (isSuccess) return;

          const requestId =
            (jqXHR && typeof jqXHR.getResponseHeader === 'function')
              ? (jqXHR.getResponseHeader('x-ms-requestid') ?? undefined)
              : undefined;
          // Structured error only. Raw `responseText` is an arbitrary body on a
          // non-JSON error response and can carry account data; the requestId
          // below is what correlates this line to the MT request.
          const errorBody = jqXHR?.responseJSON ?? jqXHR?.statusText ?? textStatus;

          activity.error(
            `[PMaxLiteSignup][AgenticOnboarding] GetRootFolder failed: skip=${skip} status=${jqXHR?.status} ${JSON.stringify(errorBody)}`,
            API_NAME,
            requestId
          );
        },
      } as any);

      const pageAssets = response?.Assets ?? [];
      allAssets.push(...pageAssets);

      // A short page means there are no more assets to fetch.
      if (pageAssets.length < PAGE_SIZE) break;
    }

    return allAssets
      .filter((asset: AssetEntity) => (
        asset?.Type === 'GenericImage'
        && !!asset?.Url
        && !!asset?.Text
        && (!filter || asset?.SourceType !== 'WebsiteImported')
      ))
      .map((asset: AssetEntity) => ({
        assetId: String(asset.Id ?? ''),
        fileName: asset.Text ?? '',
        fileUrl: asset.Url ?? '',
      }));
  }, [odataOptions, cid, aid, filter]);
};
